import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/api/client";
import { Logo } from "@/components/Logo";
import { Loader2 } from "lucide-react";

const QUICK = [
  { email: "freelancer_test@njm.local", role: "Freelancer", pw: "freelancer123" },
  { email: "partner_test@njm.local", role: "Production Partner", pw: "partner123test" },
];

const GOOGLE_ERROR_LABELS = {
  domain_not_allowed: "Only verified NJM Build accounts can sign in.",
  email_not_verified: "That Google account's email isn't verified.",
  invalid_or_expired_state: "That sign-in link expired — please try again.",
  google_denied: "Google sign-in was cancelled.",
  google_auth_failed: "Something went wrong signing in with Google — please try again.",
  njm_build_access_pending: "This Google account isn't linked to a verified NJM Build profile yet — contact NJM to get set up.",
  account_deactivated: "This account has been deactivated. Contact NJM to get set up again.",
};

export default function BuildLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("freelancer_test@njm.local");
  const [password, setPassword] = useState("freelancer123");
  const [err, setErr] = useState(GOOGLE_ERROR_LABELS[params.get("error")] || "");
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const [configError, setConfigError] = useState(false);
  const loadConfig = useCallback(() => {
    setConfigError(false);
    setConfig(null);
    api.get("/auth/config").then((r) => {
      if (!["google", "demo"].includes(r.data?.mode)) throw new Error("Invalid auth configuration");
      setConfig(r.data);
    }).catch(() => setConfigError(true));
  }, []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      // Wave 3 (2026-09) — return-url: if `Protected` (App.js) captured a `?next=` destination
      // (e.g. a shared Collaboration deep link the visitor tried to open while signed out),
      // land there instead of the generic role default. Regular login (no `next` present, the
      // overwhelmingly common case) is completely unaffected — falls through to the exact same
      // role-based defaults as before this change.
      const next = params.get("next");
      nav(next || (u?.role === "production_partner" ? "/partner/me" : u?.role === "freelancer" ? "/build/me" : "/"));
    }
    catch (ex) { const code = ex?.response?.data?.detail; setErr(GOOGLE_ERROR_LABELS[code] || code || "Login failed"); }
    finally { setBusy(false); }
  };

  const signInWithGoogle = () => {
    const next = params.get("next");
    const qs = next ? `?next=${encodeURIComponent(next)}` : "";
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/build/google/login${qs}`;
  };

  const sendMagicLink = async (e) => {
    e?.preventDefault();
    if (!magicEmail.trim()) return;
    setMagicSending(true);
    try {
      // Wave 3 (2026-09) — same `next` this page itself may have been reached with (captured by
      // `Protected` in App.js), threaded through so the emailed link returns to it after verify.
      await api.post("/v1/build/auth/magic-link/request", { email: magicEmail.trim(), next: params.get("next") || undefined });
      setMagicSent(true);
    } finally {
      setMagicSending(false);
    }
  };

  const isGoogleMode = config?.mode === "google";

  return (
    <div className="min-h-screen w-full flex flex-col lg:grid lg:grid-cols-2" data-testid="build-login-page">
      {/* Brand panel — real NJM crew photo, dark cinematic overlay, upright mark, NJM yellow accent */}
      <div className="relative h-64 sm:h-72 lg:h-auto overflow-hidden bg-ink-deep flex flex-col justify-between p-6 sm:p-12 lg:p-16">
        <img
          src="https://customer-assets.emergentagent.com/job_8bf14107-78d9-4608-90c2-1e48ddc7f859/artifacts/i1g9tqih_Screenshot%202026-07-06%20at%206.51.07%E2%80%AFAM.png"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: "center 22%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-deep via-ink-deep/75 to-ink-deep/35" />
        <div className="relative z-10">
          <Logo theme="dark" size="xl" iconOnly showBuildLabel testId="build-login-logo" />
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-editorial text-2xl sm:text-4xl lg:text-[2.75rem] leading-[1.1] sm:leading-[1.05] text-white uppercase tracking-tight">
            Talent Call For<br className="hidden lg:block" /> The Best In The Industry
          </h1>
          <p className="mt-3 sm:mt-4 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-brand">Verified Creative Network</p>
        </div>
      </div>

      {/* Sign-in panel */}
      <div className="flex items-start lg:items-center justify-center bg-canvas px-4 py-10 sm:py-14">
        <div className="w-full max-w-[380px]">
          <div className="mb-7">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-faint">Welcome back</div>
            <h2 className="font-editorial text-2xl text-ink mt-1">Sign in to NJM Build</h2>
          </div>

          {configError ? (
            <div role="alert" data-testid="build-login-config-error" className="space-y-3">
              <p className="text-sm text-ink-soft">Sign-in is temporarily unavailable. Please try again.</p>
              <button type="button" onClick={loadConfig} className="min-h-[44px] px-4 rounded-md bg-brand text-ink font-semibold" data-testid="build-login-config-retry">Retry</button>
            </div>
          ) : !config ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-faint" /></div>
          ) : isGoogleMode ? (
            <div className="space-y-4">
              {err && <div data-testid="build-login-error" className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
              {(config.build_google_configured ?? config.google_configured) ? (
                <button
                  data-testid="build-google-signin-btn"
                  onClick={signInWithGoogle}
                  className="w-full inline-flex items-center justify-center gap-2.5 rounded-md border border-hairline bg-white py-3 text-sm font-medium text-ink hover:border-hairline-strong hover:shadow-sm transition-all"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Sign in with Google
                </button>
              ) : (
                <div data-testid="build-google-not-configured" className="text-xs text-ink-soft">
                  Google sign-in isn't configured yet — set GOOGLE_OIDC_BUILD_CLIENT_ID/SECRET on the backend.
                </div>
              )}

              {!showMagicLink ? (
                <button
                  type="button"
                  data-testid="build-show-magic-link-btn"
                  onClick={() => setShowMagicLink(true)}
                  className="w-full text-center text-xs font-medium text-brand-dim hover:text-ink underline underline-offset-2 transition-colors py-1"
                >
                  Not on Gmail? Sign in with an email link
                </button>
              ) : magicSent ? (
                <div data-testid="build-magic-link-sent" className="rounded-md border border-hairline bg-canvas px-3 py-3 text-xs text-ink-soft leading-relaxed">
                  If that email is linked to an approved NJM Build freelancer profile, a sign-in link is on its way — check your inbox. It's valid for 15 minutes and works once.
                  <button type="button" onClick={() => { setMagicSent(false); setMagicEmail(""); }} data-testid="build-magic-link-resend-btn" className="block mt-2 text-brand-dim hover:text-ink underline underline-offset-2 transition-colors">
                    Use a different email
                  </button>
                </div>
              ) : (
                <form onSubmit={sendMagicLink} className="space-y-2.5 rounded-md border border-hairline bg-canvas p-3.5" data-testid="build-magic-link-form">
                  <label className="block text-[11px] font-medium uppercase tracking-wider text-faint">Email</label>
                  <input
                    type="email" required autoFocus value={magicEmail} onChange={(e) => setMagicEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="build-magic-link-email-input"
                    className="w-full rounded-md border border-hairline bg-white px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-all"
                  />
                  <button
                    type="submit" disabled={magicSending}
                    data-testid="build-magic-link-submit-btn"
                    className="w-full rounded-md bg-brand py-2.5 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-50 transition-colors"
                  >{magicSending ? "Sending…" : "Send me a sign-in link"}</button>
                  <button type="button" onClick={() => setShowMagicLink(false)} data-testid="build-magic-link-cancel-btn" className="w-full text-[11px] text-faint hover:text-ink transition-colors">Cancel</button>
                </form>
              )}

              <p className="text-[11px] text-faint leading-relaxed">For verified NJM freelancers only. Access is invitation-based.</p>
              <p className="text-[11px] text-faint leading-relaxed">
                Production Partner? <a href="/partner-portal/login" data-testid="build-to-partner-portal-link" className="text-brand-dim hover:text-ink underline underline-offset-2 transition-colors">Sign in with a secure email link →</a>
              </p>
            </div>
          ) : (
            <>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-[11px] font-medium uppercase tracking-wider text-faint mb-1.5">Email</label>
                  <input
                    data-testid="build-login-email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-md border border-hairline bg-white px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium uppercase tracking-wider text-faint mb-1.5">Password</label>
                  <input
                    data-testid="build-login-password"
                    type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-md border border-hairline bg-white px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-all"
                  />
                </div>
                {err && <div data-testid="build-login-error" className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
                <button
                  data-testid="build-login-submit"
                  disabled={busy}
                  className="w-full rounded-md bg-brand py-2.5 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-50 transition-colors"
                >{busy ? "Signing in…" : "Sign in"}</button>
              </form>

              <div className="mt-7">
                <div className="mb-2.5 text-[11px] font-medium uppercase tracking-wider text-faint">Quick sign-in (demo accounts)</div>
                <div className="flex flex-wrap gap-2">
                  {QUICK.map((q) => (
                    <button
                      key={q.email}
                      data-testid={`build-quick-${q.role.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => { setEmail(q.email); setPassword(q.pw); }}
                      className="rounded-full border border-hairline px-3 py-1 text-xs text-ink-soft hover:border-brand hover:text-ink transition-colors"
                    >{q.role}</button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="mt-9 pt-6 border-t border-hairline space-y-2">
            <a href="/login" data-testid="build-to-platform-login-link" className="block text-[11px] text-faint hover:text-ink transition-colors">
              NJM Staff? Sign in to NJM Platform →
            </a>
            <Link to="/legal" data-testid="build-login-legal-link" className="block text-[11px] text-faint hover:text-ink transition-colors">
              Privacy &amp; Terms
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
